import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ICONS, MARKETING_ORIGIN } from './constants.tsx';
import { paths, parseRoute } from './routing';
import { Screen, Brand, Project, ThumbnailConcept, GenerationParams, Platform, Emotion, Style, TextObject, Variation, SubscriptionTier } from './types';
import BrandManager from './components/BrandKitManager';
import ProjectWizard from './components/ProjectWizard';
import Editor from './components/Editor';
import LoginModal from './components/LoginModal';
import LandingPage from './components/LandingPage';
import HandoffGate from './components/HandoffGate';
import GeneratingIdeasScreen from './components/GeneratingIdeasScreen';
import CreditsDisplay from './components/CreditsDisplay';
import NotEnoughCreditsModal from './components/NotEnoughCreditsModal';
import SupportEmailLink from './components/SupportEmailLink';
import { useAuth, useCredits } from './contexts';
import { reportError } from './services/errorReporting';
import { 
  SUBSCRIPTION_TIERS,
  CREDIT_COSTS,
  getUserBrands,
  getBrand,
  createBrand,
  updateBrand,
  getAllUserProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  uploadVariationImage,
  compressImage,
  saveIdeas,
  startIdeaGeneration,
  subscribeToProject,
  getProjectIdeas,
  createPayPlusPaymentLinkCallable,
  uploadLandingRefImage,
  LANDING_REF_MAX_DATA_URL_LENGTH
} from './services/firebase';

// ---------------------------------------------------------------------------
// Granular Error Boundary fallbacks
// ---------------------------------------------------------------------------
const SidebarFallback = () => (
  <div className="p-4 text-center text-gray-500">
    <p className="mb-2 text-sm">שגיאה בטעינת הסרגל הצדדי</p>
    <button
      onClick={() => window.location.reload()}
      className="text-click-red hover:text-white text-sm underline"
    >
      רענן
    </button>
  </div>
);

const EditorFallback = () => (
  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
    <h3 className="text-xl font-bold text-white mb-2">שגיאה בעורך</h3>
    <p className="text-gray-400 mb-4">משהו השתבש. הפרויקט שלך נשמר אוטומטית.</p>
    <button
      onClick={() => (window.location.href = '/')}
      className="bg-click-red text-white px-6 py-2 rounded-lg hover:bg-white hover:text-click-red transition-all"
    >
      חזור לדשבורד
    </button>
  </div>
);

const SidebarItem = ({ 
    icon: Icon, 
    label, 
    isActive, 
    onClick 
}: { 
    icon: any, 
    label: string, 
    isActive: boolean, 
    onClick: () => void 
}) => (
    <button 
        onClick={onClick}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all mb-1 ${
            isActive 
            ? 'bg-click-card text-click-red border border-click-red/20 font-bold shadow-[inset_0_0_10px_rgba(0,0,0,0.5)]' 
            : 'text-gray-400 hover:text-white hover:bg-white/5'
        }`}
    >
        <Icon className={`w-5 h-5 ${isActive ? 'text-click-red' : ''}`} />
        {label}
    </button>
);

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const route = parseRoute(location.pathname);
  const screen = route.screen;

  // Auth & Credits
  const { user, userData, loading: authLoading, signOut, userDataError, refreshUserData, isEmailLinkUrl, emailLinkError, clearEmailLinkError, completeSignInWithEmailLink } = useAuth();
  const { credits, subscription, refreshCredits, hasEnoughCredits } = useCredits();

  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  
  // Wizard State
  const [generatedConcepts, setGeneratedConcepts] = useState<ThumbnailConcept[]>([]);
  const [currentParams, setCurrentParams] = useState<GenerationParams | null>(null);
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const generationUnsubscribeRef = useRef<(() => void) | null>(null);
  const handoffStartedRef = useRef(false);
  
  // Editor State
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeVariationIndex, setActiveVariationIndex] = useState<number>(0);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isSavingVariation, setIsSavingVariation] = useState(false);
  const [isSavingBrand, setIsSavingBrand] = useState(false);
  
  // UI State (isSelectionMode derived from route when screen === BRAND_SELECT)
  const isSelectionMode = screen === Screen.BRAND_SELECT && (route.isSelectionMode === true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsNeeded, setCreditsNeeded] = useState(10);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [renameProject, setRenameProject] = useState<Project | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [deleteConfirmProject, setDeleteConfirmProject] = useState<Project | null>(null);
  const [upgradingTier, setUpgradingTier] = useState<Exclude<SubscriptionTier, 'free'> | null>(null);
  /** Prompt from landing handoff (?prompt=...); passed to wizard as initial topic. */
  const [handoffPrompt, setHandoffPrompt] = useState<string | null>(null);
  /** Dashboard create input (main CTA). */
  /** Landing handoff ref image (data URL or Storage URL) for first project creation. */
  const [handoffRefImage, setHandoffRefImage] = useState<string | null>(null);

  const handleUpgradeToTier = useCallback(async (tier: Exclude<SubscriptionTier, 'free'>) => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    if (upgradingTier) return;
    setUpgradingTier(tier);
    try {
      const result = await createPayPlusPaymentLinkCallable({ tier, annual: false });
      const paymentPageLink = result.data?.paymentPageLink;
      if (!paymentPageLink || !/^https?:\/\//.test(paymentPageLink)) {
        throw new Error('Invalid payment link returned from server');
      }
      window.location.assign(paymentPageLink);
    } catch (error) {
      reportError(error, { module: 'payments', extra: { uid: user.uid, tier } });
      alert('לא הצלחנו ליצור קישור תשלום כרגע. נסה שוב בעוד כמה רגעים.');
    } finally {
      setUpgradingTier(null);
    }
  }, [user, upgradingTier]);

  // Load brands and projects from Firestore when user logs in
  const loadUserData = useCallback(async () => {
    if (!user) {
      setBrands([]);
      setProjects([]);
      return;
    }

    setDataLoading(true);
    try {
      const [userBrands, userProjects] = await Promise.all([
        getUserBrands(user.uid),
        getAllUserProjects(user.uid)
      ]);
      setBrands(userBrands);
      setProjects(userProjects);
    } catch (error) {
      reportError(error, { module: 'data-loading', extra: { uid: user?.uid } });
    } finally {
      setDataLoading(false);
    }
  }, [user]);

  // Load data when user changes
  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const paymentStatus = search.get('payment');
    if (!paymentStatus || !user) return;

    if (paymentStatus === 'success') {
      void Promise.all([refreshCredits(), refreshUserData()]);
      alert('התשלום התקבל בהצלחה. החשבון מתעדכן כעת.');
    } else if (paymentStatus === 'failure') {
      alert('התשלום לא הושלם. אפשר לנסות שוב בכל רגע.');
    }

    search.delete('payment');
    search.delete('intent');
    const query = search.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate, refreshCredits, refreshUserData, user]);

  /** Start project + idea generation with handoff topic and default params (no wizard). Defined before handoff useEffect so it is in scope for the dependency array. */
  const startHandoffGeneration = useCallback(async (brand: Brand, topic: string) => {
    if (!user) return;
    if (!hasEnoughCredits(CREDIT_COSTS.IMAGE_GENERATION)) {
      setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
      setShowCreditsModal(true);
      return;
    }
    const MAX_TOPIC = 500;
    const trimmedTopic = topic.slice(0, MAX_TOPIC);
    const defaultParams: GenerationParams = {
      topic: trimmedTopic,
      emotion: Emotion.SHOCK,
      style: Style.REALISTIC,
      extraDetails: '',
      platform: brand.preferredPlatform || Platform.YOUTUBE,
      hasSecondPerson: false,
      hasLogo: false,
      characterId: brand.characters.length > 0 ? brand.characters[0].id : undefined,
    };
    setCurrentParams(defaultParams);
    try {
      const gp = {
        emotion: defaultParams.emotion,
        style: defaultParams.style,
        withFace: !!defaultParams.characterId,
        ...(defaultParams.characterId && {
          characterId: defaultParams.characterId,
          characterName: brand.characters.find(c => c.id === defaultParams.characterId)?.name,
        }),
        hasSecondPerson: false,
        hasLogo: false,
      };
      const newProject = await createProject(user.uid, brand.id, {
        name: trimmedTopic,
        platform: defaultParams.platform,
        variations: [],
        generationStatus: 'pending_ideas',
        generationParams: gp,
      });
      setProjects(prev => [newProject, ...prev]);
      setActiveProjectId(newProject.id);
      setActiveVariationIndex(0);
      setIsGeneratingIdeas(true);
      setSelectedBrand(brand);

      await startIdeaGeneration({
        projectId: newProject.id,
        brandId: brand.id,
        params: {
          topic: trimmedTopic,
          platform: defaultParams.platform,
          emotion: defaultParams.emotion,
          style: defaultParams.style,
          extraDetails: '',
          characterId: defaultParams.characterId,
          hasSecondPerson: false,
          hasLogo: false,
        },
      });

      const unsub = subscribeToProject(user.uid, brand.id, newProject.id, async (project) => {
        if (!project) return;
        if (project.generationStatus === 'ideas_done') {
          generationUnsubscribeRef.current?.();
          generationUnsubscribeRef.current = null;
          try {
            const ideas = await getProjectIdeas(user.uid, brand.id, newProject.id);
            setGeneratedConcepts(ideas);
            setProjects(prev => prev.map(p => p.id === newProject.id ? { ...p, ...project } : p));
            navigate(paths.editor(brand.id, newProject.id));
          } catch (e) {
            reportError(e, { module: 'generation', extra: { uid: user?.uid, projectId: newProject.id } });
            alert('שגיאה בטעינת הרעיונות');
          } finally {
            setIsGeneratingIdeas(false);
          }
        } else if (project.generationStatus === 'ideas_error') {
          generationUnsubscribeRef.current?.();
          generationUnsubscribeRef.current = null;
          setIsGeneratingIdeas(false);
          alert('שגיאה ביצירת רעיונות, אנא נסה שנית');
        }
      });
      generationUnsubscribeRef.current = unsub;
    } catch (error) {
      reportError(error, { module: 'handoff', extra: { uid: user?.uid, brandId: brand.id } });
      setIsGeneratingIdeas(false);
      const code = error && typeof (error as { code?: string }).code === 'string' ? (error as { code: string }).code : undefined;
      if (code === 'resource-exhausted') {
        setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
        setShowCreditsModal(true);
      } else {
        alert('שגיאה ביצירת הפרויקט');
      }
    }
  }, [user, hasEnoughCredits, navigate, setCreditsNeeded, setShowCreditsModal]);

  const startHandoffGenerationRef = useRef(startHandoffGeneration);
  startHandoffGenerationRef.current = startHandoffGeneration;

  // Landing handoff: after login, read prompt + ref image, auto-create brand if needed, start generation (no wizard/brand-select screens)
  useEffect(() => {
    if (!user || dataLoading) return;
    if (handoffStartedRef.current) return;
    const search = new URLSearchParams(location.search);
    const prompt = search.get('prompt')?.trim();
    const fromLanding = search.get('from') === 'landing';
    if (!prompt && !fromLanding) return;
    handoffStartedRef.current = true;
    if (prompt) setHandoffPrompt(prompt);

    const rawRef = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('clicktube_landing_ref_image') : null;
    if (rawRef) {
      sessionStorage.removeItem('clicktube_landing_ref_image');
      if (rawRef.length > LANDING_REF_MAX_DATA_URL_LENGTH && user) {
        uploadLandingRefImage(user.uid, rawRef)
          .then((url) => setHandoffRefImage(url))
          .catch(() => setHandoffRefImage(rawRef));
      } else {
        setHandoffRefImage(rawRef);
      }
    }

    if (!prompt) return;

    if (brands.length === 0) {
      const defaultBrandData = {
        name: 'הפרויקט שלי',
        niche: 'כללי',
        targetAudience: 'כללי',
        businessDescription: '',
        preferredFont: 'Rubik' as const,
        preferredPlatform: Platform.YOUTUBE,
        characters: [],
      };
      createBrand(user.uid, defaultBrandData)
        .then((newBrand) => {
          setBrands((prev) => [...prev, newBrand]);
          setSelectedBrand(newBrand);
          startHandoffGenerationRef.current(newBrand, prompt);
        })
        .catch((err) => {
          reportError(err, { module: 'handoff', extra: { uid: user.uid } });
          navigate(paths.brandSelect(), { replace: true });
        });
    } else {
      const brand = brands[0];
      setSelectedBrand(brand);
      startHandoffGenerationRef.current(brand, prompt);
    }
  }, [user, dataLoading, location.search, navigate, brands.length, brands[0]]);

  // Allow handoff again when URL no longer has handoff params
  useEffect(() => {
    const search = new URLSearchParams(location.search);
    if (!search.get('prompt') && search.get('from') !== 'landing') handoffStartedRef.current = false;
  }, [location.search]);

  // Clear handoff prompt when done (not in wizard and not generating from handoff)
  useEffect(() => {
    if (screen !== Screen.WIZARD_INPUT && !isGeneratingIdeas) setHandoffPrompt(null);
  }, [screen, isGeneratingIdeas]);

  const prevScreenRef = useRef(screen);
  useEffect(() => {
    const cameFromOther = prevScreenRef.current !== Screen.DASHBOARD && screen === Screen.DASHBOARD;
    prevScreenRef.current = screen;
    if (cameFromOther && user) loadUserData();
  }, [screen, user, loadUserData]);

  // Clear editor-related state only when leaving Editor (avoids clearing selectedBrand on wizard and causing "Loading brand..." flash)
  const prevScreenForEditorRef = useRef(screen);
  useEffect(() => {
    const wasEditor = prevScreenForEditorRef.current === Screen.EDITOR;
    prevScreenForEditorRef.current = screen;
    if (wasEditor && screen !== Screen.EDITOR) {
      setActiveProjectId(null);
      setSelectedBrand(null);
      setCurrentParams(null);
      setGeneratedConcepts([]);
    }
  }, [screen, isGeneratingIdeas]);

  // Clear handoff ref image only when leaving Editor for dashboard (avoids clearing during handoff when still on DASHBOARD)
  const prevScreenForHandoffRef = useRef(screen);
  useEffect(() => {
    const wasEditor = prevScreenForHandoffRef.current === Screen.EDITOR;
    prevScreenForHandoffRef.current = screen;
    if (wasEditor && screen === Screen.DASHBOARD) {
      setHandoffRefImage(null);
    }
  }, [screen]);

  // URL → state: wizard (set selectedBrand from brandId)
  useEffect(() => {
    if (screen !== Screen.WIZARD_INPUT || !route.brandId || !user) return;
    const brand = brands.find(b => b.id === route.brandId);
    if (brand) {
      setSelectedBrand(brand);
      return;
    }
    if (!dataLoading) {
      if (brands.length === 0) {
        navigate(paths.brandManage());
        return;
      }
      // Brand not in cached list: try single fetch for direct links
      let cancelled = false;
      getBrand(user.uid, route.brandId).then((fetched) => {
        if (cancelled) return;
        if (fetched) {
          setSelectedBrand(fetched);
          setBrands(prev => (prev.some(b => b.id === fetched.id) ? prev : [fetched, ...prev]));
        } else {
          navigate(paths.brandManage());
        }
      }).catch(() => {
        if (!cancelled) navigate(paths.brandManage());
      });
      return () => { cancelled = true; };
    }
  }, [screen, route.brandId, user, brands, dataLoading, navigate]);

  // Editor loading state when opening from URL (direct link or refresh)
  const [editorLoading, setEditorLoading] = useState(false);

  // URL → state: editor (load project + ideas when route has brandId/projectId)
  useEffect(() => {
    if (screen !== Screen.EDITOR || !route.brandId || !route.projectId || !user) {
      if (screen !== Screen.EDITOR) setEditorLoading(false);
      return;
    }
    // Wait for brands to load before trying to resolve brand
    if (brands.length === 0 && dataLoading) return;
    const alreadyLoaded =
      activeProjectId === route.projectId && selectedBrand?.id === route.brandId;
    if (alreadyLoaded) return;

    let cancelled = false;
    setEditorLoading(true);
    (async () => {
      try {
        const [project, ideas] = await Promise.all([
          getProject(user.uid, route.brandId!, route.projectId!),
          getProjectIdeas(user.uid, route.brandId!, route.projectId!),
        ]);
        if (cancelled) return;
        if (!project) {
          navigate(paths.dashboard());
          return;
        }
        const brand = brands.find(b => b.id === route.brandId!) ?? null;
        if (!brand) {
          navigate(paths.dashboard());
          return;
        }
        const stored = project.generationParams;
        const recoveredParams: GenerationParams = {
          topic: project.name,
          platform: project.platform,
          emotion: stored?.emotion ?? Emotion.SHOCK,
          style: stored?.style ?? Style.REALISTIC,
          extraDetails: stored?.extraDetails ?? '',
          characterId: stored?.characterId,
          hasSecondPerson: stored?.hasSecondPerson ?? false,
          hasLogo: stored?.hasLogo ?? false,
        };
        setCurrentParams(recoveredParams);
        setGeneratedConcepts(ideas);
        setActiveProjectId(project.id);
        setActiveVariationIndex(Math.max(0, project.variations.length - 1));
        setSelectedBrand(brand);
        setProjects(prev => {
          const exists = prev.some(p => p.id === project.id);
          if (exists) return prev.map(p => (p.id === project.id ? project : p));
          return [project, ...prev];
        });
      } catch (_) {
        if (!cancelled) navigate(paths.dashboard());
      } finally {
        if (!cancelled) setEditorLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [screen, route.brandId, route.projectId, user?.uid, brands, dataLoading, navigate, activeProjectId, selectedBrand?.id]);

  // Subscribe to active project when in Editor so we see new variations from Cloud Function (resilient image gen)
  useEffect(() => {
    if (screen !== Screen.EDITOR || !activeProjectId || !user || !selectedBrand) return;
    const unsub = subscribeToProject(user.uid, selectedBrand.id, activeProjectId, (project) => {
      if (!project) return;
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, ...project } : p));
    });
    return () => unsub();
  }, [screen, activeProjectId, user?.uid, selectedBrand?.id]);

  // Use compact credits in header on narrow screens (e.g. < 640px)
  const [isNarrowHeader, setIsNarrowHeader] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    const q = window.matchMedia('(max-width: 639px)');
    const update = () => setIsNarrowHeader(q.matches);
    update();
    q.addEventListener('change', update);
    return () => q.removeEventListener('change', update);
  }, []);

  const handleSaveBrand = async (brand: Brand) => {
    if (!user) return;
    if (isSavingBrand) return;

    setIsSavingBrand(true);
    try {
      const existingIndex = brands.findIndex(k => k.id === brand.id);
      
      if (existingIndex >= 0) {
        // Update existing brand in Firestore
        await updateBrand(user.uid, brand.id, brand);
        const updated = [...brands];
        updated[existingIndex] = brand;
        setBrands(updated);
      } else {
        // Create new brand in Firestore
        const { id, ...brandData } = brand;
        const newBrand = await createBrand(user.uid, brandData);
        setBrands([...brands, newBrand]);
        
        // Update brand reference for selection flow
        if (screen === Screen.BRAND_SELECT && isSelectionMode) {
          setSelectedBrand(newBrand);
          navigate(paths.wizard(newBrand.id));
          return;
        }
      }
      
      if (screen === Screen.BRAND_SELECT && isSelectionMode) {
        setSelectedBrand(brand);
        navigate(paths.wizard(brand.id));
      }
    } catch (error) {
      reportError(error, { module: 'brand', extra: { uid: user?.uid } });
      alert('שגיאה בשמירת המותג');
    } finally {
      setIsSavingBrand(false);
    }
  };

  const startNewProject = () => {
    if (brands.length === 0) {
      navigate(paths.brandSelect());
    } else if (brands.length === 1) {
      navigate(paths.wizard(brands[0].id));
    } else {
      navigate(paths.brandSelect());
    }
  };

  const startDashboardFlow = (topic?: string) => {
    const trimmed = (topic ?? '').trim();
    if (trimmed) setHandoffPrompt(trimmed);
    startNewProject();
  };

  const formatRelativeTime = (createdAt: number) => {
    const diffMs = Math.max(0, Date.now() - createdAt);
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'עכשיו';
    if (minutes < 60) return `לפני ${minutes} דק׳`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `לפני ${hours} שעות`;
    const days = Math.floor(hours / 24);
    return `לפני ${days} ימים`;
  };

  const openExistingProject = (project: Project) => {
    if (!user) return;
    navigate(paths.editor(project.brandId, project.id));
  };

  /** Create project immediately and trigger server-side idea generation; listen for ideas_done. */
  const handleStartGeneration = async (params: GenerationParams) => {
    if (!user || !selectedBrand) return;
    if (!hasEnoughCredits(CREDIT_COSTS.IMAGE_GENERATION)) {
      setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
      setShowCreditsModal(true);
      return;
    }

    setCurrentParams(params);
    try {
      // Match server MAX_LENGTHS (startIdeaGeneration) to avoid 400 invalid-argument
      const MAX_TOPIC = 500;
      const MAX_EXTRA_DETAILS = 500;
      const MAX_STYLE = 100;
      const topic = params.topic.slice(0, MAX_TOPIC);
      const extraDetails = (params.extraDetails ?? '').slice(0, MAX_EXTRA_DETAILS);
      const style = (typeof params.style === 'string' ? params.style.slice(0, MAX_STYLE) : params.style) as GenerationParams['style'];

      const gp = {
        emotion: params.emotion,
        style,
        withFace: !!params.characterId,
        ...(extraDetails && { extraDetails }),
        ...(params.characterId && {
          characterId: params.characterId,
          characterName: selectedBrand.characters.find(c => c.id === params.characterId)?.name
        }),
        hasSecondPerson: params.hasSecondPerson,
        hasLogo: params.hasLogo
      };
      const newProject = await createProject(user.uid, selectedBrand.id, {
        name: topic,
        platform: params.platform,
        variations: [],
        generationStatus: 'pending_ideas',
        generationParams: gp
      });
      setProjects([newProject, ...projects]);
      setActiveProjectId(newProject.id);
      setActiveVariationIndex(0);
      setIsGeneratingIdeas(true);

      await startIdeaGeneration({
        projectId: newProject.id,
        brandId: selectedBrand.id,
        params: {
          topic,
          platform: params.platform,
          emotion: params.emotion,
          style,
          extraDetails,
          characterId: params.characterId,
          hasSecondPerson: params.hasSecondPerson,
          hasLogo: params.hasLogo
        }
      });

      const unsub = subscribeToProject(user.uid, selectedBrand.id, newProject.id, async (project) => {
        if (!project) return;
        if (project.generationStatus === 'ideas_done') {
          generationUnsubscribeRef.current?.();
          generationUnsubscribeRef.current = null;
          try {
            const ideas = await getProjectIdeas(user.uid, selectedBrand.id, newProject.id);
            setGeneratedConcepts(ideas);
            setProjects(prev => prev.map(p => p.id === newProject.id ? { ...p, ...project } : p));
            navigate(paths.editor(selectedBrand.id, newProject.id));
          } catch (e) {
            reportError(e, { module: 'generation', extra: { uid: user?.uid, projectId: newProject.id } });
            alert('שגיאה בטעינת הרעיונות');
          } finally {
            setIsGeneratingIdeas(false);
          }
        } else if (project.generationStatus === 'ideas_error') {
          generationUnsubscribeRef.current?.();
          generationUnsubscribeRef.current = null;
          setIsGeneratingIdeas(false);
          alert('שגיאה ביצירת רעיונות, אנא נסה שנית');
        }
      });
      generationUnsubscribeRef.current = unsub;
    } catch (error) {
      reportError(error, { module: 'generation', extra: { uid: user?.uid, brandId: selectedBrand?.id } });
      setIsGeneratingIdeas(false);
      const code = error && typeof (error as { code?: string }).code === 'string' ? (error as { code: string }).code : undefined;
      if (code === 'resource-exhausted') {
        setCreditsNeeded(CREDIT_COSTS.IMAGE_GENERATION);
        setShowCreditsModal(true);
      } else {
        alert('שגיאה ביצירת הפרויקט');
      }
    }
  };

  useEffect(() => {
    return () => { generationUnsubscribeRef.current?.(); };
  }, []);

  const handleAddVariation = async (newImage: string, currentTexts: TextObject[] = [], concept?: ThumbnailConcept) => {
      if (!activeProjectId || !user || !selectedBrand) return;
      
      const activeProject = projects.find(p => p.id === activeProjectId);
      if (!activeProject) return;

      setIsSavingVariation(true);

      const newVariation: Variation = {
        id: `var_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        imageUrl: '', // set below
        texts: currentTexts.map(t => ({ ...t })),
        createdAt: Date.now(),
        ...(concept && { concept }),
      };

      try {
        // Try 1: Upload to Firebase Storage (best quality, no size limit)
        let uploaded = false;
        try {
          newVariation.imageUrl = await uploadVariationImage(user.uid, activeProjectId, newImage);
          uploaded = true;
        } catch (uploadErr) {
          console.warn('Storage upload failed, compressing for inline save');
        }

        // Try 2: Compress image and save inline in Firestore
        if (!uploaded) {
          const compressed = await compressImage(newImage, 0.5, 800);
          newVariation.imageUrl = compressed;
        }

        const updatedVariations = [...activeProject.variations, newVariation];
        const newIndex = updatedVariations.length - 1;
        
        // Update local state immediately so user sees the new variation
        setProjects(prevProjects => prevProjects.map(p => {
            if (p.id === activeProjectId) {
                return { ...p, variations: updatedVariations };
            }
            return p;
        }));
        setActiveVariationIndex(newIndex);

        // Persist to Firestore
        await updateProject(user.uid, selectedBrand.id, activeProjectId, {
          variations: updatedVariations
        });
      } catch (error) {
        reportError(error, { module: 'variation', extra: { uid: user?.uid, projectId: activeProjectId } });
        alert('שגיאה בשמירת הוריאציה. נסה שוב.');
      } finally {
        setIsSavingVariation(false);
      }
  };

  const handleAutoSave = useCallback(async (variationIndex: number, texts: TextObject[]) => {
      if (!activeProjectId || !user || !selectedBrand) return;
      
      let variationsToSave: Variation[] | null = null;
      setProjects(prevProjects => {
          const activeProject = prevProjects.find(p => p.id === activeProjectId);
          if (!activeProject || variationIndex < 0 || variationIndex >= activeProject.variations.length) return prevProjects;
          variationsToSave = activeProject.variations.map((v, i) =>
            i === variationIndex ? { ...v, texts: texts } : v
          );
          return prevProjects.map(p =>
            p.id === activeProjectId ? { ...p, variations: variationsToSave! } : p
          );
      });

      if (variationsToSave) {
        try {
          await updateProject(user.uid, selectedBrand.id, activeProjectId, { variations: variationsToSave });
        } catch (error) {
          reportError(error, { module: 'auto-save', extra: { uid: user?.uid, projectId: activeProjectId } }, 'warning');
        }
      }
  }, [activeProjectId, user, selectedBrand]);

  const handleBackfillVariationConcept = useCallback(async (variationIndex: number, concept: ThumbnailConcept) => {
    if (!activeProjectId || !user || !selectedBrand) return;
    const activeProject = projects.find(p => p.id === activeProjectId);
    if (!activeProject || variationIndex < 0 || variationIndex >= activeProject.variations.length) return;
    const variationsToSave = activeProject.variations.map((v, i) =>
      i === variationIndex ? { ...v, concept } : v
    );
    setProjects(prev => prev.map(p => p.id === activeProjectId ? { ...p, variations: variationsToSave } : p));
    try {
      await updateProject(user.uid, selectedBrand.id, activeProjectId, { variations: variationsToSave });
    } catch (error) {
      reportError(error, { module: 'variation', extra: { uid: user?.uid, projectId: activeProjectId } }, 'warning');
    }
  }, [activeProjectId, user, selectedBrand, projects]);

  const getActiveProject = () => projects.find(p => p.id === activeProjectId);

  const handleRenameProject = (project: Project) => {
    setRenameProject(project);
    setRenameDraft(project.name);
  };

  const handleRenameSubmit = async () => {
    if (!user || !renameProject || !renameDraft.trim()) return;
    try {
      await updateProject(user.uid, renameProject.brandId, renameProject.id, { name: renameDraft.trim() });
      setProjects(prev => prev.map(p => p.id === renameProject.id ? { ...p, name: renameDraft.trim() } : p));
      setRenameProject(null);
      setRenameDraft('');
    } catch (error) {
      reportError(error, { module: 'project', extra: { uid: user?.uid, projectId: renameProject?.id } });
      alert('שגיאה בעדכון שם הפרויקט');
    }
  };

  const handleDeleteProjectClick = (project: Project) => {
    setDeleteConfirmProject(project);
  };

  const handleDeleteConfirm = async () => {
    const project = deleteConfirmProject;
    if (!user || !project) return;
    try {
      await deleteProject(user.uid, project.brandId, project.id);
      setProjects(prev => prev.filter(p => p.id !== project.id));
      if (activeProjectId === project.id) {
        setActiveProjectId(null);
        navigate(paths.dashboard());
      }
      setDeleteConfirmProject(null);
    } catch (error) {
      reportError(error, { module: 'project', extra: { uid: user?.uid, projectId: project?.id } });
      alert('שגיאה במחיקת הפרויקט');
    }
  };

  const getHeaderTitle = () => {
      switch (screen) {
          case Screen.DASHBOARD: return 'הפרויקטים שלי';
          case Screen.BRAND_SELECT: return isSelectionMode ? 'בחירת מותג לפרויקט' : 'ניהול מותגים';
          case Screen.WIZARD_INPUT: return `פרויקט חדש: ${selectedBrand?.name}`;
          case Screen.EDITOR: return 'עריכה ועיצוב';
          case Screen.SETTINGS: return 'הגדרות מערכת';
          default: return 'ClickTube';
      }
  };

  // Handoff gate: minimal "only sign up" screen when coming from landing
  if (!user && !authLoading) {
    const search = new URLSearchParams(location.search);
    const handoffTopic = search.get('prompt')?.trim() || null;
    const handoffFromLanding = search.get('from') === 'landing';
    if (handoffTopic || handoffFromLanding) {
      return (
        <>
          <HandoffGate handoffTopic={handoffTopic} onLoginClick={() => setShowLoginModal(true)} />
          <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
        </>
      );
    }
    return (
      <>
        <LandingPage
          onLoginClick={() => setShowLoginModal(true)}
          isEmailLinkUrl={isEmailLinkUrl}
          emailLinkError={emailLinkError}
          onClearEmailLinkError={clearEmailLinkError}
          onCompleteSignInWithEmailLink={completeSignInWithEmailLink}
          handoffTopic={handoffTopic}
          handoffFromLanding={handoffFromLanding}
        />
        <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      </>
    );
  }

  if (isGeneratingImage) {
      return (
        <div className="min-h-screen bg-click-dark flex flex-col items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-lab-pattern opacity-50"></div>
            <div className="z-10 text-center">
                <div className="relative w-32 h-32 mx-auto mb-8">
                    <div className="absolute inset-0 border-4 border-click-border rounded-full"></div>
                    <div className="absolute inset-0 border-t-4 border-click-red rounded-full animate-spin"></div>
                    <ICONS.FlaskConical className="absolute inset-0 m-auto text-white w-12 h-12 animate-pulse" />
                </div>
                <h2 className="text-4xl font-display font-bold text-white mb-2 uppercase tracking-tighter">ENGINEERING VISUALS</h2>
                <p className="text-click-red font-mono animate-pulse">Running Click-Through optimization algorithms...</p>
            </div>
        </div>
      );
  }

  const closeDrawer = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen bg-click-dark text-gray-200 font-sans selection:bg-click-red selection:text-white overflow-hidden">
      {/* Mobile backdrop: close drawer when clicking outside */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[18] md:hidden"
          onClick={closeDrawer}
          aria-hidden
        />
      )}

      <aside
        className={`w-64 bg-click-card border-l border-click-border flex flex-col shrink-0 z-20 shadow-2xl fixed top-0 right-0 h-full md:relative md:right-auto md:top-auto md:h-auto transition-transform duration-300 ease-out ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'
        }`}
      >
          <div 
            onClick={() => { navigate(paths.dashboard()); closeDrawer(); }}
            className="p-6 border-b border-click-border/50 flex items-center gap-3 cursor-pointer hover:bg-white/5 transition-colors"
          >
              <ICONS.FlaskConical className="text-click-red w-8 h-8" />
              <div>
                  <h1 className="text-xl font-display font-black tracking-widest text-white leading-none">
                      CLICK<span className="text-click-red">TUBE</span>
                  </h1>
                  <span className="text-caption text-gray-500 tracking-wider">CTR SCIENCE LAB</span>
              </div>
          </div>

          <nav className="flex-1 p-4">
              <SidebarItem icon={ICONS.LayoutGrid} label="לוח בקרה" isActive={screen === Screen.DASHBOARD} onClick={() => { navigate(paths.dashboard()); closeDrawer(); }} />
              <SidebarItem icon={ICONS.User} label="ניהול מותגים" isActive={screen === Screen.BRAND_SELECT && !isSelectionMode} onClick={() => { navigate(paths.brandManage()); closeDrawer(); }} />
              <SidebarItem icon={ICONS.Settings} label="הגדרות" isActive={screen === Screen.SETTINGS} onClick={() => { navigate(paths.settings()); closeDrawer(); }} />
          </nav>

          {/* User Profile Section */}
          {user && userData && (
            <div className="p-4 border-t border-click-border/50">
              <div className="flex items-center gap-3 mb-3">
                {userData.photoURL ? (
                  <img src={userData.photoURL} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-click-border flex items-center justify-center">
                    <ICONS.User className="w-4 h-4 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{userData.displayName || userData.email}</p>
                  <p className="text-xs text-gray-500">{SUBSCRIPTION_TIERS[subscription].nameHe}</p>
                </div>
              </div>
              <button 
                onClick={signOut}
                className="w-full flex items-center justify-center gap-2 text-gray-400 hover:text-white text-sm py-2 hover:bg-white/5 rounded transition-colors"
              >
                <ICONS.LogOut className="w-4 h-4" />
                התנתק
              </button>
            </div>
          )}

           <div className="p-4 border-t border-click-border/50">
              <button onClick={() => { startNewProject(); closeDrawer(); }} className="w-full bg-gradient-to-r from-click-red to-click-red-dim text-white font-bold py-3 rounded flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-[0_0_10px_rgba(255,0,60,0.2)]">
                  <ICONS.Plus className="w-5 h-5" /> פרויקט חדש
              </button>
          </div>
          <div className="p-4 border-t border-click-border/50 text-center">
            <SupportEmailLink className="text-xs text-gray-500 hover:text-white transition-colors" showEmail />
          </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 bg-lab-pattern relative">
          <header className="h-16 border-b border-click-border bg-click-card/50 backdrop-blur px-4 sm:px-6 md:px-8 flex items-center justify-between shrink-0 gap-2 pt-[env(safe-area-inset-top)]">
             <div className="flex items-center gap-2 min-w-0 flex-1">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="md:hidden p-2 -mr-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 shrink-0"
                  aria-label="Open menu"
                >
                  <ICONS.Menu className="w-6 h-6" />
                </button>
                {screen !== Screen.DASHBOARD && screen !== Screen.SETTINGS && screen !== Screen.BRAND_SELECT && (
                    <button onClick={() => navigate(paths.dashboard())} className="text-gray-500 hover:text-white mr-2 shrink-0">
                         <ICONS.ArrowRight className="w-5 h-5" />
                    </button>
                )}
                <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2 truncate break-words">
                {getHeaderTitle()}
             </h2>
             </div>
             <div className="flex items-center gap-3 shrink-0">
                {user ? (
                  <Sentry.ErrorBoundary fallback={<span className="text-xs text-gray-500">--</span>}>
                    <CreditsDisplay compact={isNarrowHeader} onClick={() => navigate(paths.settings())} />
                  </Sentry.ErrorBoundary>
                ) : (
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="flex items-center gap-2 text-sm text-click-cyan border border-click-cyan/30 bg-click-cyan/5 px-4 py-2 rounded-lg hover:bg-click-cyan/10 transition-colors"
                  >
                    <ICONS.LogIn className="w-4 h-4" />
                    התחבר
                  </button>
                )}
             </div>
          </header>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 scrollbar-hide">
            {screen === Screen.DASHBOARD && (
                <div className="max-w-7xl mx-auto space-y-8">
                    <div className="flex items-end justify-between gap-4 mb-2">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-1.5 h-8 rounded-full bg-click-red shrink-0" />
                        <div>
                          <h3 className="text-xl font-bold text-white break-words">הטאמבניילים שלי</h3>
                          <p className="text-xs text-gray-500">{projects.length} פרויקטים</p>
                        </div>
                      </div>
                      <button type="button" className="inline-flex items-center gap-1 text-sm text-click-red hover:text-white transition-colors">
                        הכל
                        <ICONS.ArrowRight className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div
                        onClick={() => startDashboardFlow()}
                        className="group relative bg-click-dark/40 rounded-xl border-2 border-dashed border-click-border hover:border-click-red transition-all cursor-pointer flex flex-col items-center justify-center min-h-[220px] hover:shadow-[0_0_15px_rgba(255,0,60,0.1)] hover:bg-click-card min-w-0"
                      >
                          <div className="w-16 h-16 rounded-full bg-click-border flex items-center justify-center mb-4 group-hover:bg-click-red group-hover:scale-110 transition-all duration-300">
                              <ICONS.Plus className="w-8 h-8 text-gray-500 group-hover:text-white" />
                          </div>
                          <h3 className="font-bold text-gray-400 group-hover:text-white transition-colors break-words">התחל ניסוי חדש</h3>
                      </div>

                      {projects.map(project => {
                          const lastVariation = project.variations.length > 0
                            ? project.variations[project.variations.length - 1]
                            : null;
                          const imageUrl = lastVariation?.imageUrl;
                          const brandName = brands.find(b => b.id === project.brandId)?.name || 'מותג';

                          return (
                          <div
                            key={project.id}
                            className="group relative rounded-xl overflow-hidden border border-click-red/30 bg-gradient-to-b from-click-red/20 via-click-card to-click-dark hover:border-click-red transition-all shadow-[0_0_18px_rgba(255,0,60,0.08)] min-w-0"
                          >
                              <div onClick={() => openExistingProject(project)} className="cursor-pointer">
                                  <div className="aspect-video w-full bg-black/40 relative overflow-hidden rounded-t-xl">
                                      {imageUrl ? (
                                          <img src={imageUrl} alt={project.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                      ) : (
                                          <div className="w-full h-full bg-click-dark flex items-center justify-center text-gray-600">No Image</div>
                                      )}
                                      <div className="absolute inset-0 bg-gradient-to-t from-click-dark/80 via-transparent to-transparent" />
                                  </div>
                                  <div className="p-4 rounded-b-xl relative">
                                      <div className="flex items-center gap-2 mb-2">
                                          <div className="w-1.5 h-1.5 rounded-full bg-click-cyan shrink-0"></div>
                                          <span className="text-xs text-gray-300 truncate min-w-0">{brandName}</span>
                                      </div>
                                      <div className="flex items-start gap-1 mb-2">
                                          <h3 className="font-bold text-white text-base leading-tight flex-1 min-w-0 break-words">
                                            {project.name}
                                          </h3>
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); handleRenameProject(project); }}
                                            className="p-1 rounded text-gray-400 hover:text-click-red hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
                                            aria-label="שנה שם"
                                          >
                                            <ICONS.Edit2 className="w-3.5 h-3.5" />
                                          </button>
                                      </div>

                                      <div className="flex justify-between items-center gap-2">
                                          <span className="text-caption text-gray-400">{formatRelativeTime(project.createdAt)}</span>
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); openExistingProject(project); }}
                                              className="px-3 py-1.5 rounded-md bg-click-red text-white text-xs font-bold hover:opacity-90 transition-opacity"
                                            >
                                              אתחול
                                            </button>
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); handleDeleteProjectClick(project); }}
                                              className="p-1 rounded text-gray-400 hover:text-red-300 hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
                                              aria-label="מחק פרויקט"
                                            >
                                              <ICONS.Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          </div>
                          );
                      })}
                    </div>
                </div>
            )}

            {screen === Screen.BRAND_SELECT && (
                <div className="max-w-7xl mx-auto">
                    <BrandManager brands={brands} mode={isSelectionMode ? 'select' : 'manage'} onSave={handleSaveBrand} onSelect={(brand) => navigate(paths.wizard(brand.id))} onCancel={() => navigate(paths.dashboard())} saving={isSavingBrand} />
                </div>
            )}

            {screen === Screen.WIZARD_INPUT && !selectedBrand && route.brandId && (
                <div className="max-w-7xl mx-auto flex flex-col items-center justify-center min-h-[50vh]">
                  <div className="relative w-16 h-16 mb-4">
                    <div className="absolute inset-0 border-2 border-click-border rounded-full" />
                    <div className="absolute inset-0 border-t-2 border-click-red rounded-full animate-spin" />
                  </div>
                  <p className="text-gray-400">טוען מותג...</p>
                </div>
            )}
            {screen === Screen.WIZARD_INPUT && selectedBrand && (
                <ProjectWizard brand={selectedBrand} onBack={() => navigate(paths.brandSelect())} onStartGeneration={handleStartGeneration} isGeneratingIdeas={isGeneratingIdeas} initialTopic={handoffPrompt ?? undefined} initialRefImage={handoffRefImage} />
            )}

            {screen === Screen.EDITOR && (editorLoading || !currentParams || !getActiveProject()) && (
                <div className="max-w-7xl mx-auto flex flex-col items-center justify-center min-h-[50vh]">
                  <div className="relative w-16 h-16 mb-4">
                    <div className="absolute inset-0 border-2 border-click-border rounded-full" />
                    <div className="absolute inset-0 border-t-2 border-click-red rounded-full animate-spin" />
                  </div>
                  <p className="text-gray-400">טוען פרויקט...</p>
                </div>
            )}
            {screen === Screen.EDITOR && !editorLoading && currentParams && getActiveProject() && (
                <Sentry.ErrorBoundary fallback={EditorFallback}>
                  <Editor
                      variations={getActiveProject()!.variations}
                      activeVariationIndex={activeVariationIndex}
                      onVariationIndexChange={setActiveVariationIndex}
                      onNewVariation={handleAddVariation}
                      onBackfillVariationConcept={handleBackfillVariationConcept}
                      params={currentParams}
                      brand={selectedBrand}
                      concept={getActiveProject()!.concept}
                      concepts={generatedConcepts}
                      onBack={() => navigate(paths.dashboard())}
                      onAutoSave={handleAutoSave}
                      isSaving={isSavingVariation}
                      userId={user?.uid ?? null}
                      brandId={selectedBrand?.id ?? null}
                      projectId={activeProjectId}
                      handoffReferenceImageUrl={handoffRefImage}
                  />
                </Sentry.ErrorBoundary>
            )}

            {screen === Screen.SETTINGS && (
                <div className="max-w-3xl mx-auto bg-click-card p-4 sm:p-6 md:p-8 rounded-2xl border border-click-border break-words">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="p-3 bg-click-border rounded-full"><ICONS.Settings className="w-8 h-8 text-click-red" /></div>
                        <div>
                            <h3 className="text-2xl font-bold text-white">הגדרות מערכת</h3>
                            <p className="text-gray-400">נהל את הגדרות האפליקציה והחשבון</p>
                        </div>
                    </div>
                    <div className="space-y-6">
                        {/* Account Section */}
                        {user && !userData ? (
                          <div className="p-6 border border-click-border rounded-xl bg-click-dark text-center">
                            {userDataError ? (
                              <>
                                <h4 className="text-white font-bold mb-2">שגיאה בטעינת החשבון</h4>
                                <p className="text-sm text-gray-400 mb-4">{userDataError}</p>
                                <div className="flex flex-wrap justify-center gap-3">
                                  <button
                                    type="button"
                                    onClick={() => refreshUserData()}
                                    className="bg-click-red text-white px-4 py-2 rounded-lg hover:bg-white hover:text-click-red transition-colors text-sm"
                                  >
                                    נסה שוב
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => window.location.reload()}
                                    className="text-click-red hover:text-white transition-colors text-sm"
                                  >
                                    רענן דף
                                  </button>
                                </div>
                              </>
                            ) : (
                              <>
                                <ICONS.Loader2 className="w-12 h-12 text-gray-500 mx-auto mb-4 animate-spin" />
                                <h4 className="text-white font-bold mb-2">טוען חשבון...</h4>
                                <p className="text-sm text-gray-400 mb-4">אם זה לא נטען, ייתכן שאירעה שגיאה</p>
                                <button
                                  type="button"
                                  onClick={() => window.location.reload()}
                                  className="text-click-red hover:text-white transition-colors text-sm"
                                >
                                  רענן דף
                                </button>
                              </>
                            )}
                          </div>
                        ) : user && userData ? (
                          <div className="p-6 border border-click-border rounded-xl bg-click-dark">
                            <div className="flex items-center justify-between mb-6">
                              <h4 className="text-white font-bold flex items-center gap-2">
                                <ICONS.User className="w-5 h-5 text-click-red" />
                                החשבון שלי
                              </h4>
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                subscription === 'studio' ? 'bg-yellow-400/10 text-yellow-400 border border-yellow-400/30' :
                                subscription === 'viral' ? 'bg-violet-400/10 text-violet-300 border border-violet-400/30' :
                                subscription === 'creator' ? 'bg-click-cyan/10 text-click-cyan border border-click-cyan/30' :
                                'bg-gray-500/10 text-gray-400 border border-gray-500/30'
                              }`}>
                                {SUBSCRIPTION_TIERS[subscription].nameHe}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mb-6">
                              {userData.photoURL ? (
                                <img src={userData.photoURL} alt="" className="w-16 h-16 rounded-full" />
                              ) : (
                                <div className="w-16 h-16 rounded-full bg-click-border flex items-center justify-center">
                                  <ICONS.User className="w-8 h-8 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <p className="text-lg text-white font-bold">{userData.displayName || 'משתמש'}</p>
                                <p className="text-sm text-gray-400">{userData.email}</p>
                              </div>
                            </div>
                            
                            {/* Credits Info */}
                            <div className="bg-click-card p-4 rounded-lg border border-click-border">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-gray-400 flex items-center gap-2">
                                  <ICONS.Coins className="w-4 h-4" />
                                  קרדיטים
                                </span>
                                <span className="text-white font-bold">
                                  {credits.toLocaleString()} / {SUBSCRIPTION_TIERS[subscription].monthlyCredits.toLocaleString()}
                                </span>
                              </div>
                              <div className="h-2 bg-click-border rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${
                                    (credits / SUBSCRIPTION_TIERS[subscription].monthlyCredits) > 0.5 ? 'bg-green-500' :
                                    (credits / SUBSCRIPTION_TIERS[subscription].monthlyCredits) > 0.2 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min((credits / SUBSCRIPTION_TIERS[subscription].monthlyCredits) * 100, 100)}%` }}
                                />
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                כל יצירה או עריכה של תמונה עולה 10 קרדיטים. עריכת טקסט חינמית.
                              </p>
                            </div>

                            {subscription !== 'studio' && (() => {
                              const getUpgradeOptions = () => {
                                if (subscription === 'free') return [{ tier: 'creator', ...SUBSCRIPTION_TIERS.creator }, { tier: 'viral', ...SUBSCRIPTION_TIERS.viral }, { tier: 'studio', ...SUBSCRIPTION_TIERS.studio }];
                                if (subscription === 'creator') return [{ tier: 'viral', ...SUBSCRIPTION_TIERS.viral }, { tier: 'studio', ...SUBSCRIPTION_TIERS.studio }];
                                if (subscription === 'viral') return [{ tier: 'studio', ...SUBSCRIPTION_TIERS.studio }];
                                return [];
                              };
                              const upgradeOptions = getUpgradeOptions();
                              if (upgradeOptions.length === 0) return null;
                              return (
                                <div className="mt-4 p-4 rounded-lg border border-click-cyan/30 bg-click-cyan/5">
                                  <p className="text-sm text-click-cyan mb-3">רוצה יותר קרדיטים?</p>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    {upgradeOptions.map((option) => (
                                      <button
                                        type="button"
                                        key={option.tier}
                                        onClick={() => handleUpgradeToTier(option.tier as Exclude<SubscriptionTier, 'free'>)}
                                        disabled={upgradingTier !== null}
                                        className={`p-3 rounded-xl border flex flex-col gap-1 text-right ${
                                          option.tier === 'studio'
                                            ? 'border-yellow-400/50 bg-yellow-400/5'
                                            : option.tier === 'viral'
                                            ? 'border-violet-400/50 bg-violet-400/5'
                                            : 'border-click-cyan/50 bg-click-cyan/10'
                                        } ${upgradingTier !== null ? 'opacity-70 cursor-not-allowed' : ''}`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <ICONS.Crown className={`w-4 h-4 flex-shrink-0 ${
                                            option.tier === 'studio' ? 'text-yellow-400' : option.tier === 'viral' ? 'text-violet-300' : 'text-click-cyan'
                                          }`} />
                                          <span className="font-bold text-white">{option.nameHe}</span>
                                        </div>
                                        <p className="text-gray-400 text-xs">
                                          {option.monthlyCredits.toLocaleString()} קרדיטים/חודש
                                          {option.priceMonthlyILS != null && option.priceMonthlyILS > 0 && (
                                            <span className="text-white font-medium"> · ₪{option.priceMonthlyILS}/חודש</span>
                                          )}
                                        </p>
                                        {upgradingTier === option.tier ? (
                                          <ICONS.Loader2 className="w-4 h-4 text-gray-300 animate-spin self-end mt-auto" />
                                        ) : (
                                          <ICONS.ArrowRight className="w-4 h-4 text-gray-400 rotate-180 self-end mt-auto" />
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="p-6 border border-click-border rounded-xl bg-click-dark text-center">
                            <ICONS.User className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                            <h4 className="text-white font-bold mb-2">לא מחובר</h4>
                            <p className="text-sm text-gray-400 mb-4">התחבר כדי לצפות בקרדיטים ולשמור את ההתקדמות שלך</p>
                            <button 
                              onClick={() => setShowLoginModal(true)}
                              className="bg-click-red text-white px-6 py-2 rounded-lg hover:bg-white hover:text-click-red transition-all"
                            >
                              התחבר עכשיו
                            </button>
                          </div>
                        )}

                        {/* Legal links */}
                        <div className="p-4 border border-click-border rounded-xl bg-click-dark">
                          <h4 className="text-gray-400 font-bold text-sm mb-2">משפטי</h4>
                          <div className="flex flex-wrap gap-3 text-sm">
                            <a href={`${MARKETING_ORIGIN}/privacy`} target="_blank" rel="noopener noreferrer" className="text-click-red hover:text-white transition-colors">מדיניות פרטיות</a>
                            <a href={`${MARKETING_ORIGIN}/terms`} target="_blank" rel="noopener noreferrer" className="text-click-red hover:text-white transition-colors">תנאי שימוש</a>
                            <a href={`${MARKETING_ORIGIN}/cancellation`} target="_blank" rel="noopener noreferrer" className="text-click-red hover:text-white transition-colors">מדיניות ביטולים</a>
                            <a href={`${MARKETING_ORIGIN}/cookies`} target="_blank" rel="noopener noreferrer" className="text-click-red hover:text-white transition-colors">עוגיות</a>
                          </div>
                        </div>
                    </div>
                </div>
            )}
          </div>
      </main>

      {/* Modals */}
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      <NotEnoughCreditsModal 
        isOpen={showCreditsModal} 
        onClose={() => setShowCreditsModal(false)} 
        requiredCredits={creditsNeeded}
        onUpgradeClick={handleUpgradeToTier}
        upgradingTier={upgradingTier}
      />
      {/* Rename project modal */}
      {renameProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => { setRenameProject(null); setRenameDraft(''); }}>
          <div className="bg-click-card border border-click-border rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4">שנה שם פרויקט</h3>
            <input
              type="text"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              className="w-full px-4 py-2 rounded-lg bg-click-dark border border-click-border text-white placeholder-gray-500 mb-6 focus:outline-none focus:border-click-red"
              placeholder="שם הפרויקט"
              autoFocus
            />
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => { setRenameProject(null); setRenameDraft(''); }} className="px-4 py-2 text-gray-400 hover:text-white font-bold">ביטול</button>
              <button type="button" onClick={() => renameDraft.trim() && handleRenameSubmit()} disabled={!renameDraft.trim()} className="px-4 py-2 bg-click-red text-white rounded font-bold hover:bg-white hover:text-click-red transition-colors disabled:opacity-50 disabled:cursor-not-allowed">שמור</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete project confirmation */}
      {deleteConfirmProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setDeleteConfirmProject(null)}>
          <div className="bg-click-card border border-click-border rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-2">מחק פרויקט</h3>
            <p className="text-gray-400 mb-2">האם למחוק את הפרויקט &quot;{deleteConfirmProject.name}&quot;?</p>
            <p className="text-sm text-red-400/90 mb-6">פעולה זו אינה ניתנת לביטול והפרויקט יימחק לצמיתות מהמערכת.</p>
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={() => setDeleteConfirmProject(null)} className="px-4 py-2 text-gray-400 hover:text-white font-bold">ביטול</button>
              <button type="button" onClick={handleDeleteConfirm} className="px-4 py-2 bg-red-600 text-white rounded font-bold hover:bg-red-500 transition-colors">מחק</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;